import { DocumentDataPreviewProps } from "./Document.types";
import { useTranslation } from "react-i18next";
import { FormPreview } from "../../components/form-preview";
import { useNavigate } from "react-router";
import { QdsStatusEnum, IQdsSignature } from "../../entities/IQdsRequest";
import environment from "../../config/environment";

export function DocumentDataPreview({ docType, metaDataForm, labels, tags, explicitAccess, qdsRequest, user }: DocumentDataPreviewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Determine QDS status info
  const getQdsStatusInfo = () => {
    if (!qdsRequest) {
      return {
        text: t("DOCUMENTS.QDS_PENDING"),
        backgroundColor: "#e67e22",
      };
    }

    // Check if any holder signature is signed
    const hasSignedHolder = qdsRequest.holderSignatures?.some(
      (h: IQdsSignature) => h.status === QdsStatusEnum.SIGNED
    );

    // Check if any consent is approved
    const hasApprovedConsent = qdsRequest.consents?.some(
      (c: IQdsSignature) => c.status === QdsStatusEnum.APPROVED
    );

    // If there are holder signatures, prioritize that status
    if (qdsRequest.holderSignatures && qdsRequest.holderSignatures.length > 0) {
      if (hasSignedHolder) {
        return {
          text: t("DOCUMENTS.QDS_SIGNED"),
          backgroundColor: "#28a745", // Green
        };
      }
      return {
        text: t("DOCUMENTS.QDS_PENDING_SIGNING"),
        backgroundColor: "#e67e22", // Orange
      };
    }

    // If there are consents, check that status
    if (qdsRequest.consents && qdsRequest.consents.length > 0) {
      if (hasApprovedConsent) {
        return {
          text: t("DOCUMENTS.QDS_APPROVED"),
          backgroundColor: "#28a745", // Green
        };
      }
      return {
        text: t("DOCUMENTS.QDS_PENDING_APPROVAL"),
        backgroundColor: "#e67e22", // Orange
      };
    }

    // Default
    return {
      text: t("DOCUMENTS.QDS_PENDING"),
      backgroundColor: "#e67e22", // Orange
    };
  };

  const handleQdsClick = () => {
    if (qdsRequest?.id) {
      navigate(`/qds-signatures/${qdsRequest.id}/sign`);
    }
  };
  return (
    <div className="d-flex flex-column">
      <div>
        {metaDataForm ? (
          <div>
            <h5>{t("NEW_DOCUMENT.DATA")}</h5>
            <FormPreview metaDataForm={metaDataForm} />
          </div>
        ) : null}
      </div>

      {/* QDS Signature Display */}
      {environment.modulesEnabled.qualifiedDigitalSignature && qdsRequest && (
        <div className="mt-4">
          <h5>{t("DOCUMENTS.QDS_SIGNING", "Kvalifikovani digitalni potpis")}</h5>
          <p style={{ marginBottom: "0.5rem", fontWeight: "500" }}>
            {qdsRequest.documentName}
          </p>
          <button
            className="btn"
            style={{
              backgroundColor: getQdsStatusInfo().backgroundColor,
              color: "white",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "0.25rem",
              cursor: "pointer",
              fontSize: "0.875rem",
              width: "100%",
            }}
            onClick={handleQdsClick}
          >
            {getQdsStatusInfo().text}
          </button>
        </div>
      )}

      {/* Tags Display */}
      <div className="mt-4">
        <h5>{t("DOCUMENT_TAGS.LABEL", "Tags")}</h5>
        {tags && tags.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {tags.map((tag) => (
              <div
                key={tag}
                className="badge bg-primary"
                style={{ fontSize: "0.875rem", padding: "0.5rem 0.75rem" }}
              >
                {tag}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">{t("DOCUMENT_TAGS.NONE", "None")}</p>
        )}
      </div>

      {/* Explicit Access Display */}
      <div className="mt-4">
        <h5>{t("DOCUMENT_EXPLICIT_ACCESS.LABEL")}</h5>
        {explicitAccess && explicitAccess.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {explicitAccess.map((user) => (
              <div key={user.userId} className="badge bg-primary" style={{ fontSize: "0.875rem", padding: "0.5rem 0.75rem" }}>
                {user.email}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">{t("DOCUMENT_EXPLICIT_ACCESS.NO_ACCESS")}</p>
        )}
      </div>
    </div>
  );
}
